import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreatePlan, useListPlans } from '../../api/generated/enrollment/enrollment';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { AdminOnly } from './AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from './ListOutcome';
import styles from './enrollment.module.css';

/** Maximos do contrato; o formulario nao deixa a pessoa descobri-los pelo 422. */
const MAX_CLASSES_PER_WEEK = 14;
const MAX_CLASS_DURATION_MINUTES = 60;

/** Planos de aula: cadencia contratada, sem preco — preco e da fatia financeira. */
export function PlansPage() {
  return <AdminOnly>{() => <PlansView />}</AdminOnly>;
}

function PlansView() {
  const { t } = useTranslation();
  const list = useListPlans();
  const plans = list.data ?? [];

  return (
    <div className={styles.page}>
      <h1>{t('enrollment.plans.title')}</h1>
      <p className={styles.maskHelp}>{t('enrollment.plans.noPrice')}</p>

      <ListOutcome
        isPending={list.isPending}
        isError={list.isError}
        count={plans.length}
        empty={t('enrollment.plans.empty')}
      />

      <table className={styles.table}>
        <caption className="visually-hidden">{t('enrollment.plans.title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('enrollment.plans.label')}</th>
            <th scope="col">{t('enrollment.plans.classesPerWeek')}</th>
            <th scope="col">{t('enrollment.plans.duration')}</th>
            <th scope="col">{t('enrollment.status')}</th>
          </tr>
        </thead>
        <tbody>
          {plans.map((plan) => (
            <tr key={plan.id}>
              <td>{plan.label}</td>
              <td>{plan.classesPerWeek}</td>
              <td>{t('enrollment.plans.minutes', { minutes: plan.classDurationMinutes })}</td>
              <td>{plan.active ? t('enrollment.active') : t('enrollment.inactive')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <PlanForm onCreated={() => void list.refetch()} />
    </div>
  );
}

function PlanForm({ onCreated }: { readonly onCreated: () => void }) {
  const { t } = useTranslation();
  const create = useCreatePlan();

  const [label, setLabel] = useState('');
  const [classesPerWeek, setClassesPerWeek] = useState('5');
  const [duration, setDuration] = useState('50');

  const weekly = Number(classesPerWeek);
  const minutes = Number(duration);
  const weeklyValid = Number.isInteger(weekly) && weekly >= 1 && weekly <= MAX_CLASSES_PER_WEEK;
  const durationValid =
    Number.isInteger(minutes) && minutes >= 1 && minutes <= MAX_CLASS_DURATION_MINUTES;
  const canSave = label.trim().length > 0 && weeklyValid && durationValid;

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.plans.create')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        create.mutate(
          { data: { label: label.trim(), classesPerWeek: weekly, classDurationMinutes: minutes } },
          {
            onSuccess: () => {
              setLabel('');
              onCreated();
            },
          },
        );
      }}
    >
      <h2>{t('enrollment.plans.create')}</h2>

      <Field
        label={t('enrollment.plans.label')}
        required
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />
      <Field
        label={t('enrollment.plans.classesPerWeek')}
        type="number"
        min={1}
        max={MAX_CLASSES_PER_WEEK}
        required
        value={classesPerWeek}
        error={weeklyValid ? undefined : t('enrollment.plans.classesPerWeekRange')}
        onChange={(event) => setClassesPerWeek(event.target.value)}
      />
      <Field
        label={t('enrollment.plans.duration')}
        type="number"
        min={1}
        max={MAX_CLASS_DURATION_MINUTES}
        required
        // 60 minutos e maximo absoluto do contrato, e nao apenas o default.
        hint={t('enrollment.plans.durationHint')}
        value={duration}
        error={durationValid ? undefined : t('enrollment.plans.durationRange')}
        onChange={(event) => setDuration(event.target.value)}
      />

      {create.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
      {create.isSuccess ? <Alert tone="success">{t('enrollment.plans.created')}</Alert> : null}

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('enrollment.save')}
        </Button>
      </div>
    </form>
  );
}
